// Client prediction and reconciliation (§18.2, §20.7). `predWorld` runs ahead of the server in
// predict mode with the local inputs; every snapshot rebuilds it from `authWorld` and replays the
// stored inputs, and the position error is absorbed by a decaying visual offset. Also tracks the
// input lead, the error statistics and the "pending" visuals of locally used items. DOM-free.
import { DT, MAX_BEANS, NO_SLOT, SNAPSHOT_EVERY, TUNE } from '@shared/constants';
import { EK, ItemKind } from '@shared/ids';
import { clamp, lerp } from '@shared/math';
import { applySnapshotToWorld, type Snapshot } from '@shared/protocol/snapshot';
import { createPressSet, decodePresses } from '@shared/sim/input';
import { stepWorld } from '@shared/sim/step';
import { cloneWorld, createWorld } from '@shared/sim/world';
import { NEUTRAL_INPUT, type Bean, type InputFrame, type RoundConfig, type StepContext, type Vec3, type World } from '@shared/types';
import { TICK_MS } from '../core/time';
import type { EntityView } from './FrameView';

export const PREDICTION = {
  inputRingSize: 128,
  maxStepsPerFrame: 8,
  /** A reconciliation error above this (m) resets the visual offset instead of blending it. */
  snapErrorM: 2,
  /** Time constant of the visual offset decay, seconds. */
  offsetTauSec: 0.1,
  leadMinTicks: 1,
  leadMaxTicks: 30,
  /** Lead change per snapshot per tick of input-slack error. */
  leadGain: 0.1,
  slackMinTicks: 2,
  slackMaxTicks: 6,
  /** Most ticks one hard reset fast-forwards. */
  maxFastForwardTicks: 120,
  /**
   * After a HIT on the local bean arrives, corrections decay this fast for `hitAbsorbMs`, so the
   * knockback's correction completes under the hitstop freeze (§2.13: 70–120 ms) instead of sliding
   * after it (§20.7 "absorbs the correction").
   */
  hitAbsorbTauSec: 0.025,
  hitAbsorbMs: 120,
} as const;

/** Input slack the lead aims for: `clamp(2 + ceil(jitterMs / TICK_MS), 2, 6)` ticks. */
export function slackTarget(jitterMs: number): number {
  return clamp(PREDICTION.slackMinTicks + Math.ceil(jitterMs / TICK_MS), PREDICTION.slackMinTicks, PREDICTION.slackMaxTicks);
}

// ---- Input ring ---------------------------------------------------------------------------------

/** The last 128 local input frames, by room tick. Frames are copied in, never aliased. */
export class InputRing {
  private readonly frames: InputFrame[] = Array.from({ length: PREDICTION.inputRingSize }, () => ({ tick: Number.NaN, ...NEUTRAL_INPUT }));

  private slot(tick: number): InputFrame {
    const n = PREDICTION.inputRingSize;
    return this.frames[((tick % n) + n) % n];
  }

  get(tick: number): InputFrame | null {
    const f = this.slot(tick);
    return f.tick === tick ? f : null;
  }

  /** Stores a copy of `frame` under `frame.tick` and returns the stored frame. */
  put(frame: InputFrame): InputFrame {
    return Object.assign(this.slot(frame.tick), frame);
  }

  clear(): void {
    for (const f of this.frames) f.tick = Number.NaN;
  }
}

// ---- Error statistics ---------------------------------------------------------------------------

/** Upper edges (m) of the coarse error histogram; the last bucket holds everything ≥ 2 m. */
export const PRED_ERR_HIST_EDGES: readonly number[] = [0.01, 0.02, 0.05, 0.1, 0.25, 0.5, 1, 2];
const FINE_STEP = 0.001;
const FINE_BUCKETS = Math.round(PREDICTION.snapErrorM / FINE_STEP);

export interface PredErrSummary { p50: number; p95: number; max: number; hist: number[]; count: number }
/** A plain-data copy of an ErrorStats distribution (structured-clone safe), for pooling across threads. */
export interface ErrorStatsData { fine: number[]; hist: number[]; count: number; max: number }

/** Session-wide reconciliation error distribution: 1 mm buckets up to 2 m for percentiles. */
export class ErrorStats {
  count = 0;
  max = 0;
  private readonly fine = new Uint32Array(FINE_BUCKETS + 1);
  private readonly hist: number[] = new Array<number>(PRED_ERR_HIST_EDGES.length + 1).fill(0);

  add(err: number): void {
    this.fine[Math.min(Math.floor(err / FINE_STEP), FINE_BUCKETS)]++;
    const h = PRED_ERR_HIST_EDGES.findIndex((e) => err < e);
    this.hist[h < 0 ? PRED_ERR_HIST_EDGES.length : h]++;
    this.count++;
    this.max = Math.max(this.max, err);
  }

  /** The `p`-quantile (p ∈ [0, 1]), interpolated within its 1 mm bucket; 0 with no samples. */
  percentile(p: number): number {
    if (this.count === 0) return 0;
    const rank = clamp(p, 0, 1) * this.count;
    let cum = 0;
    for (let i = 0; i <= FINE_BUCKETS; i++) {
      const c = this.fine[i];
      if (c > 0 && cum + c >= rank) return i === FINE_BUCKETS ? this.max : Math.min(this.max, (i + (rank - cum) / c) * FINE_STEP);
      cum += c;
    }
    return this.max;
  }

  summary(): PredErrSummary {
    return { p50: this.percentile(0.5), p95: this.percentile(0.95), max: this.max, hist: this.hist.slice(), count: this.count };
  }

  /** Adds every sample of `other` (pooling several clients' distributions, e.g. in the soak test). */
  merge(other: ErrorStats): void {
    for (let i = 0; i < this.fine.length; i++) this.fine[i] += other.fine[i];
    for (let i = 0; i < this.hist.length; i++) this.hist[i] += other.hist[i];
    this.count += other.count;
    this.max = Math.max(this.max, other.max);
  }

  reset(): void {
    this.fine.fill(0);
    this.hist.fill(0);
    this.count = 0;
    this.max = 0;
  }

  toData(): ErrorStatsData {
    return { fine: Array.from(this.fine), hist: this.hist.slice(), count: this.count, max: this.max };
  }

  /** Adds a distribution exported with `toData` (same bucket layout). */
  mergeData(d: ErrorStatsData): void {
    for (let i = 0; i < this.fine.length; i++) this.fine[i] += d.fine[i] ?? 0;
    for (let i = 0; i < this.hist.length; i++) this.hist[i] += d.hist[i] ?? 0;
    this.count += d.count;
    this.max = Math.max(this.max, d.max);
  }
}

// ---- Pending entities ---------------------------------------------------------------------------

interface PendingEntity { id: number; kind: EK; owner: number; x: number; y: number; z: number; vx: number; vy: number; moveTicks: number; useTick: number }

/**
 * Cosmetic stand-ins for entities the local bean spawns (§18.2: predict mode never spawns them).
 * Each one shows from its predicted use until the render timeline reaches that tick, when the
 * server entity (or its absence, after a misprediction) takes over. Ids are negative.
 */
export class PendingEntities {
  private readonly list: PendingEntity[] = [];
  private nextId = -1;

  get size(): number {
    return this.list.length;
  }

  /** A fresh predicted ITEM_USE (`item` = the event's ItemKind) by `bean` at room tick `useTick`. */
  onItemUse(item: number, bean: Bean, frame: InputFrame | null, useTick: number): void {
    const fx = Math.cos(bean.facing);
    const fy = Math.sin(bean.facing);
    const p: PendingEntity = { id: this.nextId, kind: EK.PEEL, owner: bean.slot, x: bean.pos.x, y: bean.pos.y, z: 0, vx: 0, vy: 0, moveTicks: 0, useTick };
    switch (item) {
      case ItemKind.BANANA:
        p.x -= fx * TUNE.banana.placeBehind;
        p.y -= fy * TUNE.banana.placeBehind;
        break;
      case ItemKind.BOMB:
        p.kind = EK.BOMB;
        p.z = bean.z + TUNE.grab.holdZ;
        break;
      case ItemKind.PLUNGER: {
        const aim = frame ? Math.hypot(frame.ax, frame.ay) : 0;
        const dx = frame && aim > 0 ? frame.ax / aim : fx;
        const dy = frame && aim > 0 ? frame.ay / aim : fy;
        p.kind = EK.PLUNGER;
        p.z = bean.z + TUNE.plunger.z;
        p.vx = dx * TUNE.plunger.speed;
        p.vy = dy * TUNE.plunger.speed;
        p.moveTicks = TUNE.plunger.lifeTicks;
        break;
      }
      default:
        return;
    }
    this.nextId = this.nextId <= -0x7fff ? -1 : this.nextId - 1;
    this.list.push(p);
  }

  /** Drops stand-ins whose use tick the render timeline has reached (plus one snapshot). */
  prune(renderTick: number): void {
    for (let i = this.list.length - 1; i >= 0; i--) if (renderTick >= this.list[i].useTick + SNAPSHOT_EVERY) this.list.splice(i, 1);
  }

  /** Writes the stand-ins into `out[start…]` (already sized) as of fractional room tick `localTick`. */
  write(out: EntityView[], start: number, localTick: number): void {
    for (let i = 0; i < this.list.length; i++) {
      const p = this.list[i];
      const v = out[start + i];
      const age = Math.max(0, localTick - p.useTick);
      const moved = Math.min(age, p.moveTicks) * DT;
      v.id = p.id;
      v.kind = p.kind;
      v.owner = p.owner;
      v.x = p.x + p.vx * moved;
      v.y = p.y + p.vy * moved;
      v.z = p.z;
      v.vx = age < p.moveTicks ? p.vx : 0;
      v.vy = age < p.moveTicks ? p.vy : 0;
      v.vz = 0;
      v.t = age;
      v.timer = 0;
      v.data = 0;
      v.data2 = 0;
      v.pending = true;
    }
  }

  clear(): void {
    this.list.length = 0;
  }
}

// ---- Prediction ---------------------------------------------------------------------------------

/** Lowest inactive slot (or 0): spectators step predict mode with it so every bean is a puppet. */
function spectatorSlot(cfg: RoundConfig): number {
  const i = cfg.activeSlots.findIndex((a) => !a);
  return i >= 0 ? i : 0;
}

function copyPos(out: Vec3, b: Bean): void {
  out.x = b.pos.x;
  out.y = b.pos.y;
  out.z = b.z;
}

/**
 * Prediction state of one round. Ticks named `…Tick` are room ticks; the worlds carry round ticks,
 * and the two stay a constant offset apart because both only change through steps and snapshots.
 */
export class Prediction {
  /** The last applied snapshot. */
  authWorld: World;
  predWorld: World;
  /** Room tick `predWorld` is at. */
  predTick: number;
  /** Room tick of `authWorld`; −1 before the first snapshot. */
  authTick = -1;
  /** Input lead over the server clock, ticks; NaN until `initLead`. */
  lead = Number.NaN;
  readonly localSlot: number;
  readonly inputs = new InputRing();
  readonly visualOffset: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly ctx: StepContext;
  private readonly stepInputs: (InputFrame | null)[] = new Array<InputFrame | null>(MAX_BEANS).fill(null);
  private readonly press = createPressSet();
  private readonly prevPos: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly curPos: Vec3 = { x: 0, y: 0, z: 0 };
  private readonly oldPos: Vec3 = { x: 0, y: 0, z: 0 };
  private syncedTick = Number.NEGATIVE_INFINITY;
  private dirty = false;

  /**
   * @param roundToRoom room tick minus round tick for this round (`startRoomTick`).
   * @param errors session-wide error statistics the reconciliations record into.
   */
  constructor(cfg: RoundConfig, roundId: number, roundToRoom: number, localSlot: number, private readonly errors: ErrorStats) {
    this.authWorld = createWorld(cfg, roundId, false);
    this.predWorld = cloneWorld(this.authWorld);
    this.predTick = this.authWorld.tick + roundToRoom;
    this.localSlot = localSlot;
    this.ctx = { predictSlot: localSlot !== NO_SLOT ? localSlot : spectatorSlot(cfg), rewindTicks: new Array<number>(MAX_BEANS).fill(0) };
    this.resetPos();
  }

  get hasAuth(): boolean {
    return this.authTick >= 0;
  }

  /** The predicted local bean, or null for spectators. */
  get local(): Bean | null {
    return this.localSlot === NO_SLOT ? null : this.predWorld.beans[this.localSlot];
  }

  /** Applies a newer snapshot of this round to `authWorld`; reconciliation runs on the next `reconcile`. */
  applySnapshot(snap: Snapshot): boolean {
    if (snap.roomTick <= this.authTick || !applySnapshotToWorld(this.authWorld, snap)) return false;
    this.authTick = snap.roomTick;
    this.dirty = true;
    return true;
  }

  /** Adopts an exact authoritative world from an in-process source (offline sessions, tests). */
  adoptAuthWorld(w: World, roomTick: number): boolean {
    if (roomTick <= this.authTick) return false;
    this.authWorld = cloneWorld(w);
    this.authTick = roomTick;
    this.syncedTick = roomTick;
    this.dirty = true;
    return true;
  }

  initLead(rttMs: number, jitterMs: number): void {
    this.lead = clamp(rttMs / 2 / TICK_MS + slackTarget(jitterMs), PREDICTION.leadMinTicks, PREDICTION.leadMaxTicks);
  }

  /** Per snapshot: steer the lead so the server sees `slackTarget` ticks of input slack. */
  nudgeLead(inputSlack: number, jitterMs: number): void {
    if (Number.isNaN(this.lead)) return;
    this.lead = clamp(this.lead + PREDICTION.leadGain * (slackTarget(jitterMs) - inputSlack), PREDICTION.leadMinTicks, PREDICTION.leadMaxTicks);
  }

  /**
   * After a new snapshot: rebuild `predWorld` from `authWorld` and replay the stored inputs up to
   * `predTick` (replay events are discarded). When the local bean is predicted, the change of its
   * predicted position is added to the visual offset and recorded as the prediction error.
   */
  reconcile(predictLocal: boolean): boolean {
    if (!this.dirty) return false;
    this.dirty = false;
    this.syncPrev();
    const before = this.local;
    const comparable = predictLocal && before !== null && before.active && this.predTick >= this.authTick;
    if (before) copyPos(this.oldPos, before);

    const goal = Math.max(this.predTick, this.authTick);
    this.rebuild();
    while (this.predTick < goal) this.stepOnce(predictLocal ? this.inputs.get(this.predTick + 1) : null);

    const after = this.local;
    if (comparable && after && after.active) {
      const dx = this.oldPos.x - after.pos.x;
      const dy = this.oldPos.y - after.pos.y;
      const dz = this.oldPos.z - after.z;
      const err = Math.hypot(dx, dy, dz);
      this.errors.add(err);
      this.onError?.(err, this);
      this.addVisualOffset(dx, dy, dz, err);
    }
    return true;
  }

  /** Diagnostics: receives every recorded reconciliation error with this prediction (soak probe). */
  onError: ((err: number, p: Prediction) => void) | null = null;

  /**
   * Runs fresh steps toward `targetTick` (at most 8). `sample(t)` supplies the local frame for tick
   * `t` (null: none); it is stored and, while `predictLocal`, fed to the step. `onStep` receives
   * each fresh step's events. Returns false if the 8-step budget did not catch up.
   */
  advance(targetTick: number, sample: (tick: number) => InputFrame | null, onStep: (events: World['events'], tick: number) => void, predictLocal: boolean): boolean {
    const goal = Math.floor(targetTick);
    for (let steps = 0; this.predTick < goal; steps++) {
      if (steps === PREDICTION.maxStepsPerFrame) return false;
      const t = this.predTick + 1;
      const frame = sample(t);
      const stored = frame ? this.inputs.put(frame) : null;
      this.stepOnce(predictLocal ? stored : null);
      onStep(this.predWorld.events, t);
    }
    return true;
  }

  /**
   * Hard resync: restart from `authWorld` and fast-forward (stored inputs, no events) to just
   * before `targetTick`, at most 120 ticks; the next `advance` takes the final fresh step.
   */
  hardReset(targetTick: number, predictLocal: boolean): void {
    if (!this.hasAuth) return;
    this.dirty = false;
    this.syncPrev();
    this.rebuild();
    const goal = Math.min(Math.floor(targetTick) - 1, this.authTick + PREDICTION.maxFastForwardTicks);
    while (this.predTick < goal) this.stepOnce(predictLocal ? this.inputs.get(this.predTick + 1) : null);
    this.visualOffset.x = this.visualOffset.y = this.visualOffset.z = 0;
  }

  /**
   * Adds a presentation offset of magnitude `mag` (reconciliation error or a render-source switch);
   * one above 2 m resets the offset instead (snap).
   */
  addVisualOffset(dx: number, dy: number, dz: number, mag = Math.hypot(dx, dy, dz)): void {
    const o = this.visualOffset;
    if (mag > PREDICTION.snapErrorM) {
      o.x = o.y = o.z = 0;
      return;
    }
    o.x += dx;
    o.y += dy;
    o.z += dz;
  }

  /** Per frame: `visualOffset *= exp(−dt / tau)`, tau 0.1 s by default (§20.7). */
  decay(dt: number, tauSec: number = PREDICTION.offsetTauSec): void {
    const k = Math.exp(-dt / tauSec);
    this.visualOffset.x *= k;
    this.visualOffset.y *= k;
    this.visualOffset.z *= k;
  }

  /**
   * The predicted local position between the last two predicted ticks by `alpha`. The render
   * position adds `visualOffset`.
   */
  localPos(alpha: number, out: Vec3): Vec3 {
    out.x = lerp(this.prevPos.x, this.curPos.x, alpha);
    out.y = lerp(this.prevPos.y, this.curPos.y, alpha);
    out.z = lerp(this.prevPos.z, this.curPos.z, alpha);
    return out;
  }

  private rebuild(): void {
    this.predWorld = cloneWorld(this.authWorld);
    this.predTick = this.authTick;
    this.resetPos();
  }

  private stepOnce(frame: InputFrame | null): void {
    this.stepInputs[this.ctx.predictSlot] = frame;
    this.predWorld.events.length = 0;
    stepWorld(this.predWorld, this.stepInputs, this.ctx);
    this.predTick++;
    const b = this.local;
    if (!b) return;
    this.prevPos.x = this.curPos.x;
    this.prevPos.y = this.curPos.y;
    this.prevPos.z = this.curPos.z;
    copyPos(this.curPos, b);
  }

  private resetPos(): void {
    const b = this.local;
    if (!b) return;
    copyPos(this.curPos, b);
    copyPos(this.prevPos, b);
  }

  /**
   * Snapshots do not carry press counters: fold every stored input up to `authTick` into the
   * authoritative local bean's `prev`, exactly as the server's input phase did.
   */
  private syncPrev(): void {
    if (this.localSlot === NO_SLOT) return;
    const b = this.authWorld.beans[this.localSlot];
    const from = Math.max(this.syncedTick + 1, this.authTick - PREDICTION.inputRingSize + 1);
    for (let t = from; t <= this.authTick; t++) {
      const f = this.inputs.get(t);
      if (f) decodePresses(b, f, this.press);
    }
    this.syncedTick = Math.max(this.syncedTick, this.authTick);
  }
}
